import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useOnboardingStore, OnboardingStep } from '../../stores/onboardingStore';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';

import { useAuthStore } from '../../stores/authStore';
import { useBoardStore } from '../../stores/boardStore';
import { useProfileModalStore } from '../../stores/profileModalStore';
import { useUserChannelsStore } from '../../stores/userChannelsStore';
import { useQuery } from '@tanstack/react-query';
import { boardService } from '../../lib/api/boards';

const TOOLTIP_WIDTH = 300;
const TOOLTIP_PADDING = 12;

export const OnboardingManager: React.FC = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const { status, isActive, isInitialized: isStoreInitialized, suppressedStep, completeStep, dismiss } = useOnboardingStore();
  const { user } = useAuthStore();
  const { isDataLoading, isContentModalOpen, channels, journals, specs } = useBoardStore();
  const { isOpen: isProfileOpen } = useProfileModalStore();
  const { channels: globalChannels, journals: globalJournals } = useUserChannelsStore();

  const { data: boardsResponse } = useQuery({
    queryKey: ['boards'],
    queryFn: () => boardService.getBoards(),
    enabled: !!user && isActive
  });
  const boardsCount = boardsResponse?.data?.length || 0;
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentBoardIdState, setCurrentBoardIdState] = useState<string | null>(null);
  const animationRef = useRef<number | null>(null);

  // Map steps to target IDs and routes
  const stepConfigs: Record<string, { ids: string[]; route: string | null; translationKey: string }> = {
    [OnboardingStep.WELCOME]: { ids: [], route: null, translationKey: 'welcome' },
    [OnboardingStep.CREATE_BOARD]: { ids: ['onboarding-create-board'], route: '/boards', translationKey: 'createBoard' },
    [OnboardingStep.ADD_DATA]: { 
        ids: [
            'onboarding-select-channel-modal',
            'onboarding-add-channel-modal',
            'onboarding-add-journal-modal',
            'onboarding-add-journal-modal-list',
            'onboarding-connect-channel-first',
            'onboarding-create-journal-first',
            'onboarding-add-connection-modal', 
            'onboarding-add-connection-empty'
        ], 
        route: '/boards', 
        translationKey: 'addData' 
    },
    [OnboardingStep.SET_DATE_RANGE]: { ids: ['onboarding-board-date-picker'], route: '/boards/', translationKey: 'setDateRange' },
    [OnboardingStep.ADD_SPEC]: { ids: ['onboarding-add-spec-modal'], route: '/boards/', translationKey: 'addSpec' },
    [OnboardingStep.COMPLETED]: { ids: [], route: null, translationKey: 'completed' },
  };

  const currentConfig = stepConfigs[status.currentStep];

  const updatePosition = useCallback(() => {
    if (!currentConfig || currentConfig.ids.length === 0) {
      setTargetRect(null);
      return;
    }

    let rect: DOMRect | null = null;

    for (const id of currentConfig.ids) {
        // Handle potential duplicate IDs (mobile/desktop versions) by finding the visible one
        const elements = document.querySelectorAll(`[id="${id}"]`);
        for (let i = 0; i < elements.length; i++) {
            const el = elements[i] as HTMLElement;
            const currentRect = el.getBoundingClientRect();
            if (currentRect.width > 0 && currentRect.height > 0) {
                rect = currentRect;
                break;
            }
        }
        if (rect) break;
    }

    if (rect) {
      setTargetRect(prev => {
          if (!prev) return rect;
          if (prev.top === rect!.top && prev.left === rect!.left && prev.width === rect!.width && prev.height === rect!.height) {
              return prev;
          }
          return rect;
      });
    } else {
      setTargetRect(null);
    }
    
    animationRef.current = requestAnimationFrame(updatePosition);
  }, [currentConfig?.ids]);

  useEffect(() => {
    if (isActive) {
      animationRef.current = requestAnimationFrame(updatePosition);
    } else {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      setTargetRect(null);
    }
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isActive, updatePosition]);

  useEffect(() => {
    // Determine visibility based on current step, route and target presence
    if (!isActive) {
      setIsVisible(false);
      return;
    }

    const currentStep = status.currentStep;
    if (status.completedSteps.includes(currentStep)) {
        setIsVisible(false);
        return;
    }

    if (suppressedStep === currentStep) {
        setIsVisible(false);
        return;
    }

    const config = stepConfigs[currentStep];
    if (!config) {
        setIsVisible(false);
        return;
    }

    if (currentStep === OnboardingStep.WELCOME) {
      setIsVisible(true);
      return;
    }

    // Check route match
    if (config.route) {
        if (config.route === '/boards/') {
            if (location.pathname === '/boards' || location.pathname === '/boards/') {
                setIsVisible(false);
                return;
            }
            if (!location.pathname.startsWith('/boards/')) {
                setIsVisible(false);
                return;
            }
        } else if (location.pathname !== config.route && !location.pathname.startsWith(config.route + '/')) {
            setIsVisible(false);
            return;
        }
    }

    const isOnBoardPage = location.pathname.startsWith('/boards/');
    const currentBoardId = location.pathname.split('/')[2];

    // Reset initialization when switching boards
    if (isOnBoardPage && currentBoardId && currentBoardId !== currentBoardIdState) {
        setCurrentBoardIdState(currentBoardId);
        setIsInitialized(false);
        setIsVisible(false);
        return;
    }

    // Separate initialization logic into its own effect to avoid timer reset loops during rapid re-renders (e.g. RAF)
    if (isOnBoardPage && (isDataLoading || !isInitialized)) {
        setIsVisible(false);
        return;
    }

    const isSetupModalStep = [OnboardingStep.ADD_DATA, OnboardingStep.SET_DATE_RANGE, OnboardingStep.ADD_SPEC].includes(currentStep);

    // Guard: On board page, setup steps only show if content modal is open OR we have a target (sub-modals)
    if (isOnBoardPage && isSetupModalStep && !isContentModalOpen && !targetRect) {
        setIsVisible(false);
        return;
    }


    if (config.ids.length > 0 && !targetRect) {
      setIsVisible(false);
      return;
    }

    setIsVisible(true);
  }, [isActive, status.currentStep, status.completedSteps, suppressedStep, location.pathname, targetRect, isInitialized, currentBoardIdState, isDataLoading, isContentModalOpen, channels.length, journals.length]);

  // Handle board initialization timer separately to avoid resets
  useEffect(() => {
    const isOnBoardPage = location.pathname.startsWith('/boards/');
    if (isOnBoardPage && !isDataLoading && !isInitialized && isActive) {
        const timer = setTimeout(() => setIsInitialized(true), 300);
        return () => clearTimeout(timer);
    }
  }, [location.pathname, isDataLoading, isInitialized, isActive]);

  useEffect(() => {
    // Auto-complete navigation steps if user is already beyond them
    if (!isActive) return;
    
    // Use a small timeout to avoid race conditions with state updates
    const timer = setTimeout(() => {
        const completed = status.completedSteps;

        // 1. CREATE_BOARD
        if (!completed.includes(OnboardingStep.CREATE_BOARD) && boardsCount > 0) {
            completeStep(OnboardingStep.CREATE_BOARD);
        }

        // 2. ADD_DATA (Connections/Journals)
        const hasData = channels.length > 0 || journals.length > 0 || globalChannels.length > 0 || globalJournals.length > 0;
        if (!completed.includes(OnboardingStep.ADD_DATA) && hasData) {
            completeStep(OnboardingStep.ADD_DATA);
        }

        // 3. ADD_SPEC
        if (!completed.includes(OnboardingStep.ADD_SPEC) && specs.length > 0) {
            completeStep(OnboardingStep.ADD_SPEC);
        }
    }, 200);

    return () => clearTimeout(timer);
  }, [
    boardsCount, 
    channels.length, 
    journals.length, 
    globalChannels.length, 
    globalJournals.length, 
    specs.length, 
    status.completedSteps, 
    completeStep, 
    isActive
  ]);

  const handleNext = async () => {
    await completeStep(status.currentStep);
  };

  const handleDismiss = async () => {
    await dismiss();
  };

  const getPosition = useCallback(() => {
    if (!targetRect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

    let top = targetRect.bottom + TOOLTIP_PADDING;
    let left = targetRect.left + targetRect.width / 2 - TOOLTIP_WIDTH / 2;

    // Boundary checks for horizontal
    if (left < TOOLTIP_PADDING) left = TOOLTIP_PADDING;
    if (left + TOOLTIP_WIDTH > window.innerWidth - TOOLTIP_PADDING) {
      left = window.innerWidth - TOOLTIP_WIDTH - TOOLTIP_PADDING;
    }

    // Boundary checks for vertical
    const estimatedHeight = 160; // Estimated height for tooltip
    if (top + estimatedHeight > window.innerHeight - TOOLTIP_PADDING) {
        // Try showing above
        const aboveTop = targetRect.top - TOOLTIP_PADDING - estimatedHeight;
        if (aboveTop > TOOLTIP_PADDING) {
            top = aboveTop;
        } else {
            // If neither fits perfectly, force it into the visible area
            top = Math.max(TOOLTIP_PADDING, Math.min(top, window.innerHeight - estimatedHeight - TOOLTIP_PADDING));
        }
    }

    return { top, left };
  }, [targetRect]);

  const needsSetup = !user?.currency || !user?.timezone;
  const disabledRoutes = ['/', '/login', '/register', '/checkout/success', '/plan', '/verify-email', '/check-email', '/forgot-password', '/reset-password', '/terms', '/privacy'];
  const isTourDisabledPage = disabledRoutes.includes(location.pathname);

  // Determine if the main tour logic should be active
  const shouldShowTour = isStoreInitialized && isActive && !needsSetup && !isTourDisabledPage && isVisible && !isProfileOpen;

  const pos = getPosition();

  // We always render the portal container to allow AnimatePresence to work its magic on exit
  return createPortal(
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      <AnimatePresence>
        {shouldShowTour && (
            <>
              {/* Overlay for highlights */}
              {targetRect && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-black/40 pointer-events-auto"
                  style={{
                    clipPath: `path('M 0 0 h ${window.innerWidth} v ${window.innerHeight} h -${window.innerWidth} Z M ${targetRect.left - 4} ${targetRect.top - 4} v ${targetRect.height + 8} h ${targetRect.width + 8} v -${targetRect.height + 8} Z')`,
                  }}
                />
              )}

              {/* Welcome Screen (Center Modal) */}
              {!targetRect && status.currentStep === OnboardingStep.WELCOME && (
                <div className="absolute inset-0 bg-black/60 pointer-events-auto flex items-center justify-center">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="bg-white dark:bg-neutral-900 rounded-2xl p-8 max-w-md text-center shadow-2xl border border-neutral-200 dark:border-neutral-800 pointer-events-auto"
                    >
                        <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">
                            {t(`onboarding.${stepConfigs[status.currentStep].translationKey}.title`)}
                        </h2>
                        <p className="text-neutral-600 dark:text-neutral-400 mb-8">
                            {t(`onboarding.${stepConfigs[status.currentStep].translationKey}.description`)}
                        </p>
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={handleNext}
                                className="w-full py-3 bg-accent-primary text-white rounded-xl font-bold hover:bg-accent-primary/90 transition-colors"
                            >
                                {t('onboarding.actions.next')}
                            </button>
                            <button
                                onClick={handleDismiss}
                                className="w-full py-2 text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
                            >
                                {t('onboarding.actions.skip')}
                            </button>
                        </div>
                    </motion.div>
                </div>
              )}

              {/* Tooltip for highlighted elements */}
              {targetRect && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute pointer-events-auto shadow-2xl"
                  style={{
                    top: pos.top,
                    left: pos.left,
                    width: TOOLTIP_WIDTH,
                  }}
                >
                  <div className="bg-white dark:bg-neutral-900 rounded-xl overflow-hidden border border-accent-primary/20 ring-1 ring-black/5">
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold text-accent-primary uppercase tracking-wider">
                          Tour • {Object.keys(stepConfigs).indexOf(status.currentStep) + 1} / {Object.keys(stepConfigs).length - 1}
                        </span>
                      </div>
                      <h3 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                        {t(`onboarding.${stepConfigs[status.currentStep].translationKey}.title`)}
                      </h3>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">
                        {t(`onboarding.${stepConfigs[status.currentStep].translationKey}.description`)}
                      </p>
                      <div className="flex items-center justify-between gap-2">
                          <button
                              onClick={handleDismiss}
                              className="text-[10px] font-bold text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                          >
                              {t('onboarding.actions.skip')}
                          </button>
                          <button
                              onClick={handleNext}
                              className="px-4 py-2 bg-accent-primary text-white text-xs font-bold rounded-lg hover:bg-accent-primary/90 transition-colors shadow-sm"
                          >
                              {t('onboarding.actions.next')}
                          </button>
                      </div>
                    </div>
                    <div className="h-1 bg-neutral-100 dark:bg-neutral-800 w-full overflow-hidden">
                      <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${((Object.keys(stepConfigs).indexOf(status.currentStep) + 1) / (Object.keys(stepConfigs).length - 1)) * 100}%` }}
                          className="h-full bg-accent-primary"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </>
        )}
      </AnimatePresence>
    </div>,
    document.body
  );
};
